import React, { useState } from 'react'
import { UseFormReturn } from 'react-hook-form'

import { F0DatePicker } from '@factorialco/f0-react'
import { DatePickerValue } from '@factorialco/f0-react/dist/experimental'
import {
  ShiftManagementCoverageSectionDocument,
  ShiftManagementGenerateCoverageShiftsDocument,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { i18nDateToString } from 'modules/core/lib/dates'

import { useDebounceQuery } from 'modules/shift_management/hooks/useDebouncedQuery'
import { DEBOUNCE_DELAY } from 'modules/shift_management/lib/consts/ShiftManagementCoverage'
import { DAY_IN_MS } from 'modules/shift_management/lib/consts/ShiftManagementFormats'
import { buildDateRange } from 'modules/shift_management/lib/dateFormatter'
import { SchemaType } from 'modules/shift_management/lib/forms/GenerateCoverageForm'

interface Props {
  originalStartAt: Date
  form: UseFormReturn<SchemaType>
  setAlert: (value: boolean) => void
  setShiftsAlert: React.Dispatch<React.SetStateAction<ReferencePeriodAlert>>
  shiftsAlert: ReferencePeriodAlert
  coverageAlert: ReferencePeriodAlert
  setCoverageAlert: React.Dispatch<React.SetStateAction<ReferencePeriodAlert>>
  setSubmitDisabled: (value: boolean) => void
}

interface ReferencePeriodAlert {
  value: boolean
  loading: boolean
}

const ReferencePeriod = observer(
  ({
    originalStartAt,
    form,
    setAlert,
    setShiftsAlert,
    setCoverageAlert,
    shiftsAlert,
    coverageAlert,
    setSubmitDisabled,
  }: Props) => {
    const [localDate, setLocalDate] = useState<Date>(originalStartAt)

    const { onChange: onChangeShifts } = useDebounceQuery({
      query: ShiftManagementGenerateCoverageShiftsDocument,
      buildVariables: (date) => {
        const endAt = buildDateRange(date, form.getValues('daysToGenerate') || 0, DAY_IN_MS)
        return {
          startAt: i18nDateToString(date, 'backend'),
          endAt: i18nDateToString(endAt, 'backend'),
          locationIds: form.getValues('locationIds') ?? undefined,
        }
      },
      onResolve: (result) => {
        const shiftsLength = result?.shifts?.length ?? 0
        setShiftsAlert({ value: shiftsLength <= 0, loading: false })
      },
      delay: DEBOUNCE_DELAY,
      key: 'shiftManagement',
      form,
      fieldName: 'originalStartAt',
    })
    const { onChange } = useDebounceQuery({
      query: ShiftManagementCoverageSectionDocument,
      buildVariables: (date) => {
        const endAt = buildDateRange(date, form.getValues('daysToGenerate') || 0, DAY_IN_MS)
        return {
          startAt: i18nDateToString(date, 'backend'),
          endAt: i18nDateToString(endAt, 'backend'),
          locationId: form.getValues('locationIds')?.[0] ?? undefined,
        }
      },
      onResolve: (result) => {
        const unassignedShiftsLength = result?.unassignedShifts?.length ?? 0
        setCoverageAlert({ value: unassignedShiftsLength <= 0, loading: false })
      },
      delay: DEBOUNCE_DELAY,
      key: 'shiftManagement',
      form,
      fieldName: 'originalStartAt',
    })

    useUnsafeEffect(() => {
      setShiftsAlert((prev) => ({ ...prev, loading: true }))
      setCoverageAlert((prev) => ({ ...prev, loading: true }))
      onChangeShifts(originalStartAt)
      onChange(originalStartAt)
    }, [form.getValues('locationIds')])

    useUnsafeEffect(() => {
      if (shiftsAlert.loading || coverageAlert.loading) return
      setAlert(shiftsAlert.value && coverageAlert.value)
      setSubmitDisabled(shiftsAlert.value && coverageAlert.value)
    }, [shiftsAlert, coverageAlert])

    const onSelect = (value: DatePickerValue | undefined) => {
      if (!value?.value) return
      setShiftsAlert((prev) => ({ ...prev, loading: true }))
      setCoverageAlert((prev) => ({ ...prev, loading: true }))
      setLocalDate(value.value.from)
      onChange(value.value.from)
      onChangeShifts(value.value.from)
    }

    return (
      <F0DatePicker
        label=""
        hideLabel
        onChange={onSelect}
        value={{
          granularity: 'week',
          value: {
            from: localDate,
            to: buildDateRange(localDate, form.getValues('daysToGenerate') || 0, DAY_IN_MS),
          },
        }}
      />
    )
  }
)

export { ReferencePeriod }
